import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { CertificationRequirementRestriction, CertificationSectionConfig } from 'classes-lib';
import { BullhornEntityTypes } from 'datasource-service-lib';
import { Namespace } from 'enums-lib';
import { ISection } from 'interfaces-lib';
import { IMultiselectListItem } from 'multi-select-autocomplete-lib';
import { of } from 'rxjs';
import { EntityTypeService, IEntityLabelPair } from '../../../../../services/entity-type/entity-type.service';
import { SectionComponentBase } from '../../../../sections/section-component-base';

interface IRequirementRestrictionOption {
  value: CertificationRequirementRestriction;
  label: string;
}

@Component({
  selector: 'app-certification-section',
  standalone: false,
  templateUrl: './certification-section.component.html',
  styleUrl: './certification-section.component.css',
})
export class CertificationSectionComponent extends SectionComponentBase implements OnChanges {
  @Input() section: FormGroup;
  @Input() combinedOutput = false;
  @Input() entityType = '';
  @Input() namespace = '';

  @Input() set allCertifications(items: IMultiselectListItem[]) {
    this._allCertifications = items;
  }

  @Input() set allCertificationGroups(items: IMultiselectListItem[]) {
    this._allCertificationGroups = items;
  }

  readonly restrictionModeOptions = [
    { value: 'includes', label: 'Includes' },
    { value: 'excludes', label: 'Excludes' },
  ];

  requirementRestrictionOptions: IRequirementRestrictionOption[] = [
    { value: CertificationRequirementRestriction.None, label: 'None' },
  ];

  readonly certificationQueryFn = (q: string) => of(this.filterItems(this._allCertifications ?? [], q));
  readonly certificationGroupQueryFn = (q: string) => of(this.filterItems(this._allCertificationGroups ?? [], q));

  private static readonly DIGIT_REGEX = /^\d+$/;
  private static readonly COMMA_SEPARATED_DIGITS_REGEX = /^\d*(\s*,\s*\d*)+$/;

  private _allCertifications: IMultiselectListItem[] | undefined = undefined;
  private _allCertificationGroups: IMultiselectListItem[] | undefined = undefined;

  constructor(private entityTypeService: EntityTypeService) {
    super();
  }

  get certificationsLoaded(): boolean {
    return this._allCertifications !== undefined && this._allCertificationGroups !== undefined;
  }

  get config(): FormGroup {
    return this.section.get('config') as FormGroup;
  }

  get displayStatusControl(): FormControl {
    return this.config.get('displayStatusRestrictions') as FormControl;
  }

  get requirementRestrictionControl(): FormControl {
    return this.config.get('requirementRestriction') as FormControl;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['entityType'] || changes['namespace']) {
      this.updateRequirementRestrictionOptions();
    }
  }

  get initialCertificationValues(): IMultiselectListItem[] {
    return ((this.config.get('certificationRestrictions')?.value as number[]) ?? []).map((id) => {
      const match = (this._allCertifications ?? []).find((c) => c.id === id);
      return match ?? { id, label: String(id) };
    });
  }

  get initialCertificationGroupValues(): IMultiselectListItem[] {
    return ((this.config.get('certificationGroupRestrictions')?.value as number[]) ?? []).map((id) => {
      const match = (this._allCertificationGroups ?? []).find((c) => c.id === id);
      return match ?? { id, label: String(id) };
    });
  }

  onCertificationRestrictionsChange(items: IMultiselectListItem[]) {
    const control = this.config.get('certificationRestrictions')!;
    control.setValue(items.map((i) => i.id));
    control.markAsDirty();
  }

  onCertificationGroupRestrictionsChange(items: IMultiselectListItem[]) {
    const control = this.config.get('certificationGroupRestrictions')!;
    control.setValue(items.map((i) => i.id));
    control.markAsDirty();
  }

  private updateRequirementRestrictionOptions(): void {
    if (!this.namespace) {
      return;
    }

    this.entityTypeService.getEntitiesForNamespace(this.namespace as Namespace).subscribe((entities) => {
      const labelFor = (objectName: string) => this.findEntityLabel(entities, objectName) ?? objectName;
      const candidateLabel = labelFor(BullhornEntityTypes.Candidate);

      const options: IRequirementRestrictionOption[] = [{ value: CertificationRequirementRestriction.None, label: 'None' }];
      if (this.entityType === BullhornEntityTypes.Candidate) {
        options.push({ value: CertificationRequirementRestriction.All, label: `All ${candidateLabel} requirements` });
      } else if (this.entityType === BullhornEntityTypes.JobSubmission) {
        const jobSubmissionLabel = labelFor(BullhornEntityTypes.JobSubmission);
        options.push({ value: CertificationRequirementRestriction.All, label: `All ${candidateLabel} requirements` });
        options.push({ value: CertificationRequirementRestriction.JobSubmission, label: `${jobSubmissionLabel} requirements only` });
      } else if (this.entityType === BullhornEntityTypes.Placement) {
        const placementLabel = labelFor(BullhornEntityTypes.Placement);
        options.push({ value: CertificationRequirementRestriction.All, label: `All ${candidateLabel} requirements` });
        options.push({ value: CertificationRequirementRestriction.Placement, label: `${placementLabel} requirements only` });
      }

      this.requirementRestrictionOptions = options;

      const control = this.requirementRestrictionControl;
      if (control && !options.some((option) => option.value === control.value)) {
        control.setValue(CertificationRequirementRestriction.None);
      }
    });
  }

  private findEntityLabel(entities: IEntityLabelPair[], objectName: string): string | undefined {
    return entities.find((entity) => entity.objectName === objectName)?.label;
  }

  private filterItems(items: IMultiselectListItem[], keyword: string): IMultiselectListItem[] {
    const k = keyword?.trim();
    if (!k) return items;
    if (CertificationSectionComponent.DIGIT_REGEX.test(k)) {
      return items.filter((i) => i.id === +k);
    }
    if (CertificationSectionComponent.COMMA_SEPARATED_DIGITS_REGEX.test(k) && k.includes(',')) {
      const ids = k
        .split(',')
        .map((s) => +s.trim())
        .filter(Boolean);
      return items.filter((i) => ids.includes(i.id));
    }
    return items.filter((i) => i.label.toLowerCase().includes(k.toLowerCase()));
  }

  public static override buildForm(formGroup: UntypedFormGroup, section?: ISection): void {
    const config = section?.config
      ? CertificationSectionConfig.clone(section.config as CertificationSectionConfig)
      : new CertificationSectionConfig();

    const configGroup = new UntypedFormGroup({
      required: new UntypedFormControl(config.required),
      certificationRestrictions: new UntypedFormControl(config.certificationRestrictions),
      certificationRestrictionsMode: new UntypedFormControl(config.certificationRestrictionsMode),
      certificationGroupRestrictions: new UntypedFormControl(config.certificationGroupRestrictions),
      certificationGroupRestrictionsMode: new UntypedFormControl(config.certificationGroupRestrictionsMode),
      saveBackToDataSource: new UntypedFormControl(config.saveBackToDataSource ?? true),
      displayStatusRestrictions: new UntypedFormControl(config.displayStatusRestrictions),
      requirementRestriction: new UntypedFormControl(config.requirementRestriction ?? CertificationRequirementRestriction.None),
      autoSelectFiles: new UntypedFormControl(config.autoSelectFiles ?? false),
    });

    formGroup.setControl('config', configGroup);
    formGroup.get('required').setValue(false);
  }
}
